import { useTranslation } from 'react-i18next'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { ProcessorDetails } from '@/lib/api/processors'
import { formatMoney } from "@/lib/money"

interface AuthorizeNetConfigurationCardProps {
    processor: ProcessorDetails
}

const formatDate = (date: Date) => {
    return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    })
}

const getApiLoginId = (options: Record<string, unknown> | null) => {
    return options?.apiLoginId as string | undefined
}

const getClientKey = (options: Record<string, unknown> | null) => {
    return options?.clientKey as string | undefined
}

const getWebhookUrl = (options: Record<string, unknown> | null) => {
    return options?.webhookUrl as string | undefined
}

const getTestMode = (options: Record<string, unknown> | null) => {
    return options?.testMode === true
}

export const AuthorizeNetConfigurationCard = ({ processor }: AuthorizeNetConfigurationCardProps) => {
    const { t } = useTranslation('processors')
    const apiLoginId = getApiLoginId(processor.options)
    const clientKey = getClientKey(processor.options)
    const webhookUrl = getWebhookUrl(processor.options)
    const testMode = getTestMode(processor.options)

    return (
        <Card className="mb-6">
            <CardHeader>
                <CardTitle>{t('authorizenet.configuration.title')}</CardTitle>
            </CardHeader>
            <CardContent>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <p className="text-sm text-muted-foreground">{t('apiLoginId')}</p>
                        <p className="font-mono text-sm">{apiLoginId || t('notConfigured')}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('transactionKey')}</p>
                        <p className="font-mono text-sm">••••••••••••••••</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('environment')}</p>
                        <Badge variant={testMode ? 'secondary' : 'default'}>
                            {testMode ? t('sandbox') : t('production')}
                        </Badge>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('status')}</p>
                        <Badge variant={processor.enabled ? 'default' : 'destructive'}>
                            {processor.enabled ? t('active') : t('inactive')}
                        </Badge>
                    </div>

                    {processor.baseCurrency && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('baseCurrency')}</p>
                            <p className="font-semibold">{processor.baseCurrency}</p>
                        </div>
                    )}

                    {processor.monthlyProcessingCapacity !== null && (
                        <div>
                            <p className="text-sm text-muted-foreground">{t('monthlyProcessingCapacity')}</p>
                            <p className="font-semibold">
                                {formatMoney(processor.monthlyProcessingCapacity, processor.baseCurrency || 'USD')}
                            </p>
                        </div>
                    )}

                    <div>
                        <p className="text-sm text-muted-foreground">{t('createdAt')}</p>
                        <p className="font-semibold">{formatDate(processor.createdAt)}</p>
                    </div>

                    <div>
                        <p className="text-sm text-muted-foreground">{t('updatedAt')}</p>
                        <p className="font-semibold">{formatDate(processor.updatedAt)}</p>
                    </div>

                    {processor.supportedCurrencies && processor.supportedCurrencies.length > 0 && (
                        <div className="col-span-full">
                            <p className="text-sm text-muted-foreground mb-2">{t('supportedCurrencies')}</p>
                            <div className="flex flex-wrap gap-2">
                                {processor.supportedCurrencies.map((currency) => (
                                    <Badge key={currency} variant="outline">
                                        {currency}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    )}

                    {testMode && (
                        <div className="col-span-full">
                            <div className="rounded-lg bg-yellow-50 border border-yellow-200 p-4">
                                <div className="flex">
                                    <div className="flex-shrink-0">
                                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                        </svg>
                                    </div>
                                    <div className="ml-3">
                                        <h3 className="text-sm font-medium text-yellow-800">
                                            {t('authorizenet.configuration.testModeTitle')}
                                        </h3>
                                        <div className="mt-2 text-sm text-yellow-700">
                                            <p>
                                                {t('authorizenet.configuration.testModeWarning')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </CardContent>
        </Card>
    )
}
